import * as React from 'react';
import { clsx } from 'clsx';

export interface ListRowProps {
  /**
   * The main value/content text (required)
   */
  value: string;
  /**
   * Optional label text displayed on the left
   */
  label?: string;
  /**
   * Optional note text displayed on the right (italic)
   */
  note?: string;
  /**
   * Show the right arrow icon - enables hover state and indicates row is clickable
   * @default false
   */
  icon?: boolean;
  /**
   * Click handler - typically used with icon prop for navigation
   */
  onClick?: () => void;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Additional inline styles
   */
  style?: React.CSSProperties;
}

// Caret/Arrow icon - transforms on hover
const CaretIcon = ({ isHovered }: { isHovered: boolean }) => {
  if (isHovered) {
    // Arrow pointing right on hover
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M6 4L10 8L6 12"
          stroke="#2f2f2f"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  // Default caret (chevron right)
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M6 4L10 8L6 12"
        stroke="#b3b3b3"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// Arbor Design System list row styles
const listRowStyles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    borderBottom: '1px solid #f8f8f8',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    boxSizing: 'border-box' as const,
    borderRadius: '8px',
    transition: 'background-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
    outline: 'none',
  },
  containerHover: {
    backgroundColor: '#f8f8f8',
  },
  containerFocus: {
    outline: '3px solid #3cad51',
    outlineOffset: '-3px',
  },
  containerClickable: {
    cursor: 'pointer',
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '4px 8px',
    minHeight: '28px',
  },
  label: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#2f2f2f',
    lineHeight: '1.5',
    width: '190px',
    flexShrink: 0,
  },
  valueContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
  },
  value: {
    fontSize: '13px',
    fontWeight: '400',
    color: '#2f2f2f',
    lineHeight: '1.5',
  },
  rightContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px',
    flexShrink: 0,
  },
  note: {
    fontSize: '13px',
    fontWeight: '400',
    fontStyle: 'italic',
    color: '#2f2f2f',
    lineHeight: '1.5',
    textAlign: 'right' as const,
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    flexShrink: 0,
  },
};

/**
 * ListRow component - Arbor Design System
 *
 * A flexible list row component for displaying content within sections.
 * Supports optional label, note, and icon props.
 * Hover state is only enabled when icon is present (indicating clickable row).
 *
 * @example
 * ```tsx
 * // Basic row with just value
 * <ListRow value="John Smith" />
 *
 * // With label
 * <ListRow label="Name" value="John Smith" />
 *
 * // With label and note
 * <ListRow label="Name" value="John Smith" note="Primary contact" />
 *
 * // Clickable row with icon
 * <ListRow
 *   label="Name"
 *   value="John Smith"
 *   icon
 *   onClick={() => navigate('/user/123')}
 * />
 *
 * // All props
 * <ListRow
 *   label="Name"
 *   value="John Smith"
 *   note="View profile"
 *   icon
 *   onClick={() => navigate('/user/123')}
 * />
 * ```
 */
export const ListRow = React.forwardRef<HTMLDivElement, ListRowProps>(
  ({ value, label, note, icon = false, onClick, className, style, ...props }, ref) => {
    const [isHovered, setIsHovered] = React.useState(false);
    const [isFocused, setIsFocused] = React.useState(false);

    // Only enable hover state when icon is present
    const showHoverState = icon && isHovered;
    const isClickable = icon && onClick;

    const containerStyle: React.CSSProperties = {
      ...listRowStyles.container,
      ...(showHoverState && listRowStyles.containerHover),
      ...(isFocused && listRowStyles.containerFocus),
      ...(isClickable && listRowStyles.containerClickable),
      ...style,
    };

    const handleClick = () => {
      if (isClickable && onClick) {
        onClick();
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (isClickable && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        onClick?.();
      }
    };

    return (
      <div
        ref={ref}
        className={clsx('arbor-list-row', className)}
        style={containerStyle}
        onClick={handleClick}
        onMouseEnter={() => icon && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onKeyDown={handleKeyDown}
        tabIndex={isClickable ? 0 : undefined}
        role={isClickable ? 'button' : undefined}
        {...props}
      >
        <div style={listRowStyles.content}>
          {label && <div style={listRowStyles.label}>{label}</div>}
          <div style={listRowStyles.valueContainer}>
            <span style={listRowStyles.value}>{value}</span>
          </div>
          <div style={listRowStyles.rightContainer}>
            {note && <span style={listRowStyles.note}>{note}</span>}
            {icon && (
              <div style={listRowStyles.iconWrapper}>
                <CaretIcon isHovered={isHovered} />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

ListRow.displayName = 'ListRow';
